import { useEffect, useRef, useState } from 'react';

import { isApp, loadJsHecateScript, uniframeReady } from '@spot-fe/utils';
import { UserInfoState, useSetUserInfo } from '@spot-fe/base-ui';

interface ILoginRegisterCommon {
  setProperty: (key: string, value: any) => IRegisterModal;
  subscribeEvent: (event: string, callback: any) => IRegisterModal;
  dealloc: () => void;
}
export interface IRegisterModal extends ILoginRegisterCommon {
  events: {
    HIDE_MODAL: string;
    REGISTER_SUCCESS: string;
  };
  properties: {
    VISIBLE: string;
    EMAIL: string;
    PHONE: string;
    HIDE_LOGIN: string;
    IS_INVITE_PAGE: string;
  };
}

export interface ILoginModal extends ILoginRegisterCommon {
  events: {
    HIDE_MODAL: string;
    LOGIN_SUCCESS: string;
  };
  properties: {
    VISIBLE: string;
    HIDE_REGISTER: string;
    IS_INVITE_PAGE: string;
  };
}

export interface ModalOptions {
  showRegister?: boolean;
  onRegisterSuccessCb?: () => void;
  onLoginSuccessCb?: (userInfo?: UserInfoState) => void;
}

const registerId = '__bybit_uniframe_register__';

const initRegisterWrapDom = (registerId: string) => {
  const registerWrapDom = document.createElement('div');
  registerWrapDom.id = registerId;
  document.body.appendChild(registerWrapDom);
  return registerWrapDom;
};

export function useLoginRegisterModal() {
  const setUserInfo = useSetUserInfo();
  const [isLoading, setIsLoading] = useState(false);
  const [readyState, setReadyState] = useState(-1);
  const registerRef = useRef<IRegisterModal>();
  const loginRef = useRef<ILoginModal>();
  const registerDOMRef = useRef<HTMLDivElement>();
  const modalOptionRef = useRef<ModalOptions>();

  const onRegisterSuccess = () => {
    modalOptionRef.current?.onRegisterSuccessCb?.();
  };

  const closeRegisterModal = () => {
    const registerRefCurr = registerRef.current;
    if (!registerRefCurr) return;
    const { VISIBLE, EMAIL, PHONE } = registerRefCurr.properties;
    registerRefCurr
      .setProperty(PHONE, {})
      .setProperty(EMAIL, '')
      .setProperty(VISIBLE, false);
  };

  const onRegisterModalClose = () => {
    closeRegisterModal();
  };

  const closeLoginModal = () => {
    loginRef.current?.setProperty(loginRef.current.properties.VISIBLE, false);
  };

  const onLoginModalClose = () => {
    closeLoginModal();
  };

  const onLoginSuccess = (userInfo) => {
    setUserInfo(userInfo);
    closeLoginModal();
    setTimeout(() => {
      window.location.reload();
    }, 500);
    modalOptionRef.current?.onLoginSuccessCb?.(userInfo);
  };

  useEffect(() => {
    return () => {
      registerRef.current?.dealloc();
      loginRef.current?.dealloc();
      registerDOMRef.current?.remove();
    };
  }, []);

  const init = async () => {
    await uniframeReady();
    return new Promise((resolve, reject) => {
      (window as any)?.BybitUniFrame?.ready(({ registerModal, loginModal }) => {
        registerDOMRef.current = initRegisterWrapDom(registerId);
        registerModal
          .init({ containerId: registerId })
          .then((register: IRegisterModal) => {
            setReadyState(1);
            const { REGISTER_SUCCESS, HIDE_MODAL } = register.events;
            registerRef.current = register;
            register
              .subscribeEvent(HIDE_MODAL, onRegisterModalClose)
              .subscribeEvent(REGISTER_SUCCESS, onRegisterSuccess);
            resolve(true);
          })
          .catch(() => {
            setReadyState(0);
            reject();
          });

        loginModal
          .init()
          .then((login: ILoginModal) => {
            setReadyState(1);
            loginRef.current = login;
            const { HIDE_MODAL, LOGIN_SUCCESS } = login.events;
            login
              .subscribeEvent(HIDE_MODAL, onLoginModalClose)
              .subscribeEvent(LOGIN_SUCCESS, onLoginSuccess);
            resolve(true);
          })
          .catch(() => {
            setReadyState(0);
            reject();
          });
      });
    });
  };

  const openAppLogin = async () => {
    try {
      await loadJsHecateScript();
      (window as any)?.BybitHecate?.module('UserProfile')
        .method('getUserProfile')
        .invoke({ needLogin: true })
        .then((res) => {
          // 当前页面刷新
          modalOptionRef.current?.onLoginSuccessCb?.();
          setTimeout(() => {
            window.location.reload();
          }, 500);
        })
        .catch(() => {});
    } catch (error) {
      console.error(error);
    }
  };

  const setVisible = async (modalOption?: ModalOptions) => {
    // 判断app
    if (isApp()) {
      await openAppLogin();
      return;
    }
    try {
      modalOptionRef.current = modalOption;
      if (readyState !== 1) {
        setIsLoading(true);
        await init();
      }
      const { showRegister } = modalOption || {};
      if (showRegister) {
        if (registerRef.current) {
          const { VISIBLE } = registerRef.current.properties;
          registerRef.current.setProperty(VISIBLE, true);
        }
      } else {
        if (loginRef.current) {
          const { VISIBLE } = loginRef.current.properties;
          loginRef.current.setProperty(VISIBLE, true);
        }
      }

      setIsLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  return {
    openLoginRegisterModal: setVisible,
    loginRegisterModalInitLoading: isLoading,
  };
}
